import { DateTime } from 'luxon';
import React from 'react';
import {
  Datagrid,
  ReferenceManyField,
  TextField,
  usePermissions,
  Identifier,
  useRecordContext,
  DateField,
  useGetOne,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from '@mui/material';

import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import hasAccess from '../../utilities/hasAccess';
import toStockholmTime from '../../utilities/toStockholmTime';
import CreateCustomConnectionButton from './CreateCustomConnectionButton';
import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import RemoveCustomConnectionButton from './RemoveCustomConnectionButton';
import { EntityTypes } from '../../constants/entityTypes';
import actions, { Actions } from '../../constants/actions';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import { listViewDatagridStyle } from '../../constants/style/datagridStyles';
import PermissionMissing from '../utils/PermissionMissing';
import { requestTypes } from '../../constants/requestTypes';

type Connection = {
  id: Identifier;
  from: {
    id: Identifier;
    type: EntityTypes;
  };
  to: {
    id: Identifier;
    type: EntityTypes;
  };
  type: 'custom';
  createdAt: string;
  connectionInfo: unknown;
}

const ConnectedEntityField = ({
  parentId,
  label,
}: {
  parentId: Identifier;
  label: string;
}) => {
  const record = useRecordContext<Connection>();

  if (!record) return null;

  let source;
  if (record.from.id === parentId) source = 'to';
  else if (record.to.id === parentId) source = 'from';

  if (!source) return null;

  return (
    <ReferenceEntityField source={source} label={label}>
      <EntityDisplayNameField />
    </ReferenceEntityField>
  );
};

type FormattedStats = {
  outgoing: {
    frequency: number;
    volume: number;
  };
  incoming: {
    frequency: number;
    volume: number;
  };
  processing: {
    frequency: number;
    volume: number;
  };
  total: {
    frequency: number;
    volume: number;
  };
  id: string;
}

// eslint-disable-next-line no-empty-pattern
const ConnectedEntityTransactionCount = ({
  parentId,
}: {
  parentId: Identifier;
  label: string;
}) => {
  const record = useRecordContext();

  const dateTo = toStockholmTime(DateTime.now()).toISODate();
  const dateFrom = toStockholmTime(DateTime.now()).minus({ days: 30 }).toISODate();
  const entityId = record?.from.id === parentId ? record?.to.id : record?.from.id;
  const entityType = record?.from.id === parentId ? record?.to.type : record?.from.type;
  const { data, isLoading } = useGetOne<FormattedStats>('requests/graph', { id: `/stats?entityId=${entityId}&entityType=${entityType}&requestType=${requestTypes.TRANSACTION}&from=${dateFrom}&to=${dateTo}` }, { enabled: !!record });
  if (isLoading || !record) return null;
  return (
    <Typography>
      {data?.total.frequency ?? 0}
    </Typography>
  );
};

const ConnectedEntityCount = ({
  parentId,
}: {
  parentId: Identifier;
}) => {
  const { data, isLoading } = useGetOne<{ id: string; count: string }>('connections/count', { id: `${parentId}?type=custom` }, { enabled: !!parentId });
  if (isLoading || !data) return <Typography variant="h6">(0)</Typography>;

  return (
    <Typography variant="h6">
      (
      {data.count}
      )
    </Typography>
  );
};

const RelatedCustomConnections = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  if (!record) return null;
  return (
    <Card variant="outlined">
      <CardHeader
        title={(
          <Box display="flex" flexDirection="row" gap={1} padding={0}>
            <Typography variant="h6">Custom connections</Typography>
            { hasAccess(permissions, actions.CONNECTION_LIST) && (
              <ConnectedEntityCount parentId={record.id} />)}
          </Box>
        )}
        action={hasAccess(permissions, actions.CONNECTION_CREATE) && (
          <CreateCustomConnectionButton />
        )}
      />
      <Divider />
      <CardContent>
        { hasAccess(permissions, actions.CONNECTION_LIST) ? (
          <ReferenceManyField
            reference="connections"
            target="entity"
            filter={{ type: 'custom' }}
            perPage={15}
            pagination={<Pagination disableEmptyText />}
          >
            <Datagrid sx={listViewDatagridStyle} bulkActionButtons={false} empty={<NoResults />}>
              <ConnectedEntityField parentId={record.id} label="Connected entity" />
              <TextField sortable={false} source="connectionInfo.description" label="Description" />
              <DateField sortable={false} source="createdAt" label="Created At" />
              <ConnectedEntityTransactionCount parentId={record.id} label="30-day transaction count" />
              <RemoveCustomConnectionButton />
            </Datagrid>
          </ReferenceManyField>
        ) : (
          <Box display="flex" alignItems="center" width="100%" flexDirection="column">
            <PermissionMissing />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RelatedCustomConnections;
